import { Container, Sprite, Texture } from "pixi.js";
import { gsap } from "gsap";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { WheelRewardContainer } from "../WheelRewardContainer";
import { WedgeRewardData } from "../../../model/WedgeRewardData";
import { WedgeRewardType } from "../../../model/WedgeRewardType";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { WheelFrameAnimation } from "../goldenwheel/WheelFrameAnimation";
import { IPiggyWheelModel } from "../../../model/IPiggyWheelModel";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
type Timeline = gsap.core.Timeline;

export class PiggyWheel extends Container {
    private static readonly MAX_DEGREES = 360;
    private static readonly DEGREES_TO_RADIANS = Math.PI / (PiggyWheel.MAX_DEGREES / 2);
    private static readonly WHEEL_ROTATIONS_NUMBER = 5;
    private static readonly WHEEL_ROTATE_TIME = 6.5;

    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly canvasScaler: ICanvasScaler;
    private readonly logger: ILogger;
    private wheelBg: Sprite;
    private wheelLight: Sprite;
    private wheelArrow: Sprite;
    private currentWedgeIndex: number;
    private wheelFrameAnimation: WheelFrameAnimation;

    public constructor(
        wheelResources: HourlyBonusResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        fortuneWheelModel: IFortuneWheelModel,
        canvasScaler: ICanvasScaler,
        logger: ILogger
    ) {
        super();
        this.wheelResources = wheelResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.fortuneWheelModel = fortuneWheelModel;
        this.canvasScaler = canvasScaler;
        this.logger = logger;
        this.currentWedgeIndex = 0;
        this.build();
    }

    private build() {
        this.wheelLight = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_glow_png);
        this.wheelLight.anchor.set(0.5, 0.5);
        this.addChild(this.wheelLight);
        this.wheelBg = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_base_png);
        this.wheelBg.eventMode = "static";
        this.wheelBg.anchor.set(0.5, 0.5);
        this.addChild(this.wheelBg);
        this.wheelFrameAnimation = new WheelFrameAnimation(this.canvasScaler, this.wheelResources);
        this.addChild(this.wheelFrameAnimation);
        this.wheelArrow = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_center_png);
        this.wheelArrow.anchor.set(0.5, 0.5);
        this.wheelArrow.position.y = -107.16;
        this.addChild(this.wheelArrow);
        this.buildRewards();
    }

    private buildRewards(): void {
        let angle = 0;
        const wheelRewards = this.fortuneWheelModel.piggyWheelModel.value.wedges.value;
        wheelRewards.forEach((rewardDto) => {
            const radians = angle * PiggyWheel.DEGREES_TO_RADIANS;
            const rewardData = this.fortuneWheelModel.piggyWheelModel.value.getWedgeRewardData(rewardDto);
            if (rewardDto.isBonusWedge) {
                const wedgeBg = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_sector_bg_png);
                wedgeBg.anchor.set(0.5);
                wedgeBg.position.set(Math.cos(radians - Math.PI / 2), Math.sin(radians - Math.PI / 2));
                wedgeBg.rotation = radians;
                this.wheelBg.addChild(wedgeBg);
            }
            const wheelRewardContainer = new WheelRewardContainer(this.getWedgeRewardIcon(rewardData, rewardDto.isBonusWedge), rewardData.count);
            wheelRewardContainer.position.set(Math.cos(radians - Math.PI / 2) * 180, Math.sin(radians - Math.PI / 2) * 180);
            wheelRewardContainer.rotation = radians;
            angle += PiggyWheel.MAX_DEGREES / wheelRewards.length;
            this.wheelBg.addChild(wheelRewardContainer);
        });
    }

    private getWedgeRewardIcon(rewardData: WedgeRewardData, isBonusWedge: boolean): Texture {
        switch (rewardData.type) {
            case WedgeRewardType.Coin: return this.commonResources.assets_common_ui_top_panel_coin_png;
            case WedgeRewardType.Shield: return this.commonResources.assets_common_ui_top_panel_shield_png;
            case WedgeRewardType.DiceRoll: return !isBonusWedge ? this.wheelResources.assets_views_hourly_bonus_dice_png : this.wheelResources.assets_views_hourly_bonus_dice_2_png;
            default:
                this.logger.warn(`Reward type not implemented`);
                return Texture.EMPTY;
        }
    }

    public playWheelGlowAnimation(): void {
        this.wheelFrameAnimation.play();
    }

    public hideWheelFrameAnimation(): void {
        this.wheelFrameAnimation.visible = false;
    }

    public async rotateWheel(): Promise<void> {
        const rewardWedgeIndex = this.fortuneWheelModel.piggyWheelModel.value.rewardWedgeIndex;
        const targetWedgeIndex = rewardWedgeIndex < this.currentWedgeIndex ?
            this.fortuneWheelModel.piggyWheelModel.value.wedges.value.length - this.currentWedgeIndex + rewardWedgeIndex :
            Math.abs(rewardWedgeIndex - this.currentWedgeIndex);
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.wheelBg, {
            rotation: this.wheelBg.rotation + ((PiggyWheel.MAX_DEGREES * PiggyWheel.WHEEL_ROTATIONS_NUMBER * PiggyWheel.DEGREES_TO_RADIANS) -
                (targetWedgeIndex * PiggyWheel.MAX_DEGREES / this.fortuneWheelModel.piggyWheelModel.value.wedges.value.length * PiggyWheel.DEGREES_TO_RADIANS)),
            duration: PiggyWheel.WHEEL_ROTATE_TIME,
            onStart: () => this.soundResources.assets_sounds_goldenwheel_new_wheel_golden_wheel_spin_v2_mp3.play(),
            ease: "Cubic.easeOut"
        });
        this.currentWedgeIndex = rewardWedgeIndex;
        await animationTimeLine.play();
        animationTimeLine.kill();
    }
}
